import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Breadcrumbs,
  Link,
  LinearProgress,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import SaveIcon from "@mui/icons-material/Save";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import jwtAuthKioskAxios from "../../../services/auth/jwtAuthKiosk";
import { FRONTEND_TYPE } from "../../../utils/constants/settings";
import { setBackendDomain } from "app/services/api/management";

const AddNewCustomer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({
    company_name: "",
    status: true, // Default active
    street: "",
    city: "",
    country: "sk", // Default SK
    zip: "",
    partner: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    beDomain: "https://staging.gastrokiosk.online", // Default staging
    storeCode: "",
    frontendType: "",
    settings: {
      takeAway: true,
      dineIn: true,
    },
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    // Special handling for zip field - only allow numbers and max 5 digits
    if (field === "zip") {
      // Remove any non-numeric characters
      const numericValue = value.replace(/\D/g, "");
      // Limit to 5 digits
      value = numericValue.slice(0, 5);
    }

    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = t("validation.requiredField");
    }
    if (!formData.street.trim()) {
      newErrors.street = t("validation.requiredField");
    }
    if (!formData.city.trim()) {
      newErrors.city = t("validation.requiredField");
    }
    if (!formData.country) {
      newErrors.country = t("validation.requiredField");
    }
    if (!formData.zip.trim()) {
      newErrors.zip = t("validation.requiredField");
    } else if (!/^\d{1,5}$/.test(formData.zip.trim())) {
      newErrors.zip = t("validation.zipFormat");
    }
    if (!formData.partner) {
      newErrors.partner = t("validation.requiredField");
    }
    if (!formData.email.trim()) {
      newErrors.email = t("validation.requiredField");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("validation.requiredField");
    }
    if (!formData.first_name.trim()) {
      newErrors.first_name = t("validation.requiredField");
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = t("validation.requiredField");
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t("validation.requiredField");
    }
    if (!formData.beDomain.trim()) {
      newErrors.beDomain = t("validation.requiredField");
    }
    if (!formData.storeCode.trim()) {
      newErrors.storeCode = t("validation.requiredField");
    }
    if (!formData.frontendType.trim()) {
      newErrors.frontendType = t("validation.requiredField");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const simulateProgress = () => {
    return new Promise((resolve) => {
      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress += Math.random() * 15 + 5; // Random increment between 5-20%
        if (currentProgress >= 100) {
          currentProgress = 100;
          setProgress(100);
          clearInterval(interval);
          setTimeout(resolve, 300); // Small delay when reaching 100%
        } else {
          setProgress(currentProgress);
        }
      }, 200); // Update every 200ms
    });
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      // Start progress simulation
      const progressPromise = simulateProgress();

      // Make API call
      setBackendDomain(jwtAuthKioskAxios);
      const apiPromise = jwtAuthKioskAxios.post(
        "/dsok/customer/create",
        formData
      );

      // Wait for both to complete
      const [, response] = await Promise.all([progressPromise, apiPromise]);

      enqueueSnackbar(t("notifications.customerCreatedSuccessfully"), {
        variant: "success",
      });

      // Small delay before navigation to show 100% completion
      setTimeout(() => {
        navigate("/dsok/customers");
      }, 500);
    } catch (error) {
      console.error("Error creating customer:", error);
      enqueueSnackbar(
        error.response?.data?.message ||
          t("notifications.customerCreationFailed"),
        {
          variant: "error",
        }
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 500);
    }
  };

  const handleBackToCustomers = () => {
    navigate("/dsok/customers");
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleBreadcrumbClick("/");
          }}
          sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          {t("sidebar.menuItem.home")}
        </Link>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleBreadcrumbClick("/dsok/customers");
          }}
          sx={{ textDecoration: "none" }}
        >
          {t("sidebar.menuItem.customers")} - DSOK
        </Link>
        <Typography color="text.primary">
          {t("pages.customer.newCustomerForm")}
        </Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t("pages.customer.newCustomerForm")}
        </Typography>
      </Box>

      {/* Progress Dialog */}
      <Dialog
        open={loading}
        disableEscapeKeyDown
        disableAutoFocus
        sx={{
          "& .MuiDialog-paper": {
            minWidth: 400,
            padding: 3,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center" }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t("pages.customer.creatingCustomer")}
          </Typography>
          <Box sx={{ width: "100%", mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {Math.round(progress)}%
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Form Content */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("pages.customer.basicInformation")}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Company Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.companyName") + " *"}
                value={formData.company_name}
                onChange={(e) =>
                  handleInputChange("company_name", e.target.value)
                }
                error={!!errors.company_name}
                helperText={errors.company_name}
                variant="outlined"
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.status}
                    onChange={(e) =>
                      handleInputChange("status", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label={
                  formData.status
                    ? t("pages.customer.active")
                    : t("pages.customer.inactive")
                }
              />
            </Grid>

            {/* Street */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.street") + " *"}
                value={formData.street}
                onChange={(e) => handleInputChange("street", e.target.value)}
                error={!!errors.street}
                helperText={errors.street}
                variant="outlined"
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.city") + " *"}
                value={formData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                error={!!errors.city}
                helperText={errors.city}
                variant="outlined"
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.country}>
                <InputLabel>{t("pages.customer.country")} *</InputLabel>
                <Select
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                  label={t("pages.customer.country") + " *"}
                >
                  <MenuItem value="sk">{t("pages.customer.slovakia")}</MenuItem>
                  <MenuItem value="cz">
                    {t("pages.customer.czechRepublic")}
                  </MenuItem>
                </Select>
                {errors.country && (
                  <Typography variant="caption" color="error">
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* ZIP */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.zipCode") + " *"}
                value={formData.zip}
                onChange={(e) => handleInputChange("zip", e.target.value)}
                error={!!errors.zip}
                helperText={errors.zip}
                variant="outlined"
              />
            </Grid>

            {/* Partner */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.partner}>
                <InputLabel>{t("pages.customer.partner")} *</InputLabel>
                <Select
                  value={formData.partner}
                  onChange={(e) => handleInputChange("partner", e.target.value)}
                  label={t("pages.customer.partner") + " *"}
                >
                  <MenuItem value="1">Simpleo</MenuItem>
                  <MenuItem value="2">Libor Buchta</MenuItem>
                  <MenuItem value="40">Abiset</MenuItem>
                  <MenuItem value="56">BlueTouch Media SK, s. r. o.</MenuItem>
                </Select>
                {errors.partner && (
                  <Typography variant="caption" color="error">
                    {errors.partner}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.email") + " *"}
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                variant="outlined"
              />
            </Grid>

            {/* First Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.firstName") + " *"}
                value={formData.first_name}
                onChange={(e) =>
                  handleInputChange("first_name", e.target.value)
                }
                error={!!errors.first_name}
                helperText={errors.first_name}
                variant="outlined"
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.lastName") + " *"}
                value={formData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                error={!!errors.last_name}
                helperText={errors.last_name}
                variant="outlined"
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.phone") + " *"}
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                variant="outlined"
              />
            </Grid>

            {/* Backend Domain */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.beDomain}>
                <InputLabel>{t("pages.customer.backendDomain")} *</InputLabel>
                <Select
                  value={formData.beDomain}
                  onChange={(e) =>
                    handleInputChange("beDomain", e.target.value)
                  }
                  label={t("pages.customer.backendDomain") + " *"}
                >
                  <MenuItem value="https://staging.gastrokiosk.online">
                    Staging - https://staging.gastrokiosk.online
                  </MenuItem>
                  <MenuItem value="https://be.gastrokiosk.online">
                    Production - https://be.gastrokiosk.online
                  </MenuItem>
                </Select>
                {errors.beDomain && (
                  <Typography variant="caption" color="error">
                    {errors.beDomain}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Store Code */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pages.customer.storeCode") + " *"}
                value={formData.storeCode}
                onChange={(e) => handleInputChange("storeCode", e.target.value)}
                error={!!errors.storeCode}
                helperText={errors.storeCode}
                variant="outlined"
              />
            </Grid>

            {/* Frontend Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.frontendType}>
                <InputLabel>{t("pages.customer.frontendType")} *</InputLabel>
                <Select
                  value={formData.frontendType}
                  onChange={(e) =>
                    handleInputChange("frontendType", e.target.value)
                  }
                  label={t("pages.customer.frontendType") + " *"}
                >
                  {FRONTEND_TYPE.map((option) => (
                    <MenuItem key={option.code} value={option.code}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.frontendType && (
                  <Typography variant="caption" color="error">
                    {errors.frontendType}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Navigation Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleBackToCustomers}
                  disabled={loading}
                >
                  {t("buttons.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreate}
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading
                    ? t("pages.customer.creating")
                    : t("pages.customer.createCustomer")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddNewCustomer;
